<template lang="pug">
    div
        .form__title
            slot(name='title')
        form.form__body(action='javascript://' v-if='params')
            template(v-for="(example, index) in params.examples")
                .form__field(v-if="gameMode !== 'course' && gameMode !== 'generator-settings'")
                    label.form__label
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_numOfExamples') }}
                    .form__field-body.form__field-body--inline
                        cs-spin-input(
                            v-model="example.num"
                            :min="1"
                            :max="100"
                            @increase="example.num++"
                            @decrease="example.num--"
                        )
                        .remove-icon(v-if="index > 0" @click="removeExamples(index)")
                            svg-icon(name="cancel", size="x-large")
                .form__field(v-if="gameMode !== 'course' && gameMode !== 'generator-settings'")
                    label.form__label
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_operations') }}
                    .form__field-body
                        cs-multiselect(
                          :options="operations"
                          v-model="example.operations"
                          :placeholder="operationsLabel(example.operations)"
                          :custom-label="operationsLabel"
                          :multiple="false"
                          :allow-empty="false"
                      )
                    span.help.is-danger(v-show="errors.has('operations')")
                        |  {{  $i18n.t('trainer.trainer_gameFormErrors_chooseOperation')  }}
                divider
                .row
                    .col-md-9.col-xs-12
                        .divisible-multiplier
                            span
                                template(v-if='example.numbers.length  ===  0')
                                    |   {{ $i18n.t('trainer.trainer_gameFormSetting_divisibleMultiplier')  }}
                                template(v-else='')
                                    |   {{ $i18n.t('trainer.trainer_gameFormSetting_dividerMultiplier') }}
                        .form__field_columns.form__field_numbers
                            template(v-for='number in numbers')
                                cs-checkbox.checkbox-for-numbers(
                                    :checked="example.numbers && example.numbers.includes(number)",
                                    @change="setNumber(number, index)"
                                    :disabled="example.operations.divide && number === 0"
                                )
                                    |   {{ number }}
                        span.help.is-danger(v-show="errors.has('numbers')")
                            |  {{  $i18n.t('trainer.trainer_gameFormSetting_select')  }}
                            template(v-if='example.numbers.length  ===  0')
                                |  {{  $i18n.t('trainer.trainer_gameFormSetting_divisibleMultiplier')  }}
                            template(v-else)
                                |  {{  $i18n.t('trainer.trainer_gameFormSetting_dividerMultiplier')  }}
            .form__field
                .form__label
                .form__body
                    cs-button(
                        @click="addExamples()"
                        :disabled="params.examples.length > 100"
                    )
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_addExample') }}
            divider
            slot(name='parameters')
            slot(name='help')
            .form__buttons(v-if='!usersIds')
                cs-button.form__button(@click='cancel', color="blue", pattern="primary-outline")
                    | {{ $i18n.t('trainer.trainer_gameFormSetting_dontSave') }}
                template(v-if="gameMode === 'free-game'")
                    cs-button.form__button(color="blue", @click='saveGameMode' :disabled="isSaveGameDisabled")
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_saveGame') }}
                template(v-else='')
                    cs-button.form__button(color="blue", @click='saveGame' :disabled="isSaveGameDisabled")
                        | {{ $i18n.t('trainer.trainer_gameFormSetting_saveGame') }}
            .form__buttons(v-else='')
                cs-button.form__button(@click='cancel', color="blue", pattern="primary-outline")
                    | {{ $i18n.t('trainer.trainer_gameFormSetting_dontSave') }}
                cs-button.form__button(color="blue", @click='saveGame' :disabled="isSaveGameDisabled")
                    | {{ $i18n.t('trainer.trainer_gameFormSetting_saveDifference') }}
</template>

<script>
    import GameFormMixin from '../../../utils/trainer/GameFormMixin';
    import TrainerGames from '../../../config/trainer.json';
    import { mapState } from 'vuex';
    import { cloneDeep, kebabCase } from 'lodash';
    import multiplayerFormMixin from '../../../utils/multiplayer/formMixin';

    export default {
        props: {
            slug: String,
            courseType: String,
            id: [Number, String],
            usersIds: [Array, String],
            homeworkId: [Number, String],
            gameMode: String,
            gameId: [Number, String],
            generatorGameIndex: Number,
            generatorSettings: Boolean,
            levelAnswerDuration: Number,
        },
        mixins: [GameFormMixin, multiplayerFormMixin],
        data() {
            return {
                examples: [],
                params: null,
                operations: [
                    {
                        divide: false,
                        multiply: true,
                    },
                    {
                        divide: true,
                        multiply: false,
                    },
                ],
            };
        },
        async mounted() {
            if (this.gameMode === 'free-game') {
                const paramsArray = this.$store.state.trainer.paramsArray;
                if (
                    paramsArray &&
                    paramsArray[0] &&
                    paramsArray[0].slug === this.slug &&
                    this.$store.state.trainer.game?.courseType &&
                    this.courseType &&
                    this.courseType === this.$store.state.trainer.game.courseType
                ) {
                    this.paramsArray = cloneDeep(paramsArray);
                    this.$emit('playersNumber', this.paramsArray.length);
                    this.params = this.paramsArray[0];
                } else {
                    const courseTypeKebab = kebabCase(this.courseType);
                    const params = TrainerGames.games.find(
                        ({ slug, courseType }) => slug === this.slug && courseType === courseTypeKebab
                    ).parameters.default;
                    this.params = cloneDeep(params);
                }
            } else {
                this.setInitParams(this.getKey(this.usersIds));
            }
            this.params.examples.length || this.addExamples();
        },
        methods: {
            setNumber(number, exampleIndex) {
                if (this.params.examples[exampleIndex].numbers.includes(number)) {
                    const numberIndex = this.params.examples[exampleIndex].numbers.indexOf(number);
                    this.params.examples[exampleIndex].numbers.splice(numberIndex, 1);
                } else {
                    this.params.examples[exampleIndex].numbers.push(number);
                }
            },
            operationsLabel(operations) {
                if (operations.multiply) {
                    return this.$i18n.t('trainer.trainer_gameFormSetting_operationMultiply');
                }

                return this.$i18n.t('trainer.trainer_gameFormSetting_operationDivide');
            },
            addExamples() {
                this.params.examples.push({
                    num: 1,
                    operations: cloneDeep(this.operations[0]),
                    numbers: [2],
                });
            },
            removeExamples(index) {
                this.params.examples.splice(index, 1);
            },
            getTranslatedName({ id }) {
                return this.translatedNameById[id];
            },
        },
        computed: {
            ...mapState({
                homework: state => state.trainer.homework,
                gamesParameters: state => state.trainer.gamesParameters,
                user: state => state.auth.user,
            }),
            numbers() {
                return Array.from(Array(11), (_, i) => i);
            },
            isSaveGameDisabled() {
                let isDisabled = false;
                this.params.examples.forEach(example => {
                    if (example.numbers.length === 0) {
                        isDisabled = true;
                    }
                });
                return isDisabled;
            },
        },
        watch: {
            'params.examples': {
                handler(newVal) {
                    newVal.forEach(example => {
                        const zeroIndex = example.numbers.indexOf(0);
                        if (example.operations.divide && zeroIndex !== -1) {
                            example.numbers.splice(zeroIndex, 1);
                        }
                    });
                },
                deep: true,
            },
        },
    };
</script>

<style lang="scss" scoped>
    .form__title {
        font-weight: 500;
        font-size: 22px;
        line-height: 26px;
        text-align: center;
        margin-bottom: 25px;
    }

    .form__body {
        width: 100%;
        box-sizing: border-box;
        @include media($lg) {
            padding: 0 4.5%;
        }
    }

    .form__field {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        @include media($md) {
            padding: 0 2.5px;
            flex-direction: row;
            align-items: center;
        }
        @include media($lg) {
            padding: 0 10%;
        }
        @include media($xlg) {
            padding: 0 13%;
        }
    }
    .form__label {
        min-width: 0;
        font-size: 16px;
        line-height: 19px;
        margin-right: 20px;
        margin-bottom: 0.5rem;
        @include media($md) {
            width: 8em;
            margin-bottom: 0;
            text-align: right;
        }
        @include media($xlg) {
            max-width: none;
            min-width: 13em;
        }
    }

    .form__field-body {
        flex-grow: 5;
    }

    .form__field_numbers {
        flex-direction: row;
        flex-wrap: wrap;
        width: 100%;
        margin-bottom: 0;
    }

    .form__field-body--inline {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        justify-content: space-between;
    }

    .form__field-body--inline-md {
        flex-direction: column;
        @include media($md) {
            align-items: baseline;
            flex-direction: row;
            flex: 1 0;
        }
    }

    .form__field_columns {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        @include media($md) {
            padding: 0 2.5px;
            flex-direction: row;
            align-items: center;
        }
    }

    .remove-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        color: $x-color-black;
        &:hover {
            color: $hsl-color-red-62;
        }
    }

    .form__multiselect-container {
        width: 100%;
        @include media($md) {
            width: auto;
            flex: 1 1 auto;
        }
    }

    .form__multiselect-container:not(:last-child) {
        margin-bottom: 20px;
        @include media($md) {
            margin: 0 10px 0 0;
        }
    }

    .form__buttons {
        margin-top: 30px;
        display: flex;
        flex-direction: column;
        justify-content: center;
        @include media($md) {
            flex-direction: row;
        }
    }

    .form__button {
        margin: 10px 50px;
        @include media($md) {
            margin: 0 10px;
        }
    }

    .divisible-multiplier {
        margin: 10px 5px;
    }

    .checkbox-for-numbers {
        margin-right: 15px;
        margin-bottom: $space-small !important;
    }
</style>
